import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Trash2, CheckCircle, AlertTriangle, XCircle, ChevronRight, Minus, ChevronLeft } from 'lucide-react';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { Card } from '../components/SubscriptionAppCard';
import { StatCard } from '../components/StatCard';
// --- Mock Data ---
const IMPORT_RESULTS = [{
  id: 1,
  email: 'sarah.j@example.com',
  name: 'Sarah Johnson',
  plan: 'Reserve Club',
  status: 'Active',
  joinDate: '2023-01-15',
  result: 'Imported',
  reason: null
}, {
  id: 2,
  email: 'mike.smith@test.com',
  name: 'Mike Smith',
  plan: 'Red Only',
  status: 'Active',
  joinDate: '2022-11-01',
  result: 'Merged',
  reason: 'Email already existed — records merged'
}, {
  id: 3,
  email: 'emma.w@winery.com',
  name: 'Emma Williams',
  plan: 'Reserve Club',
  status: 'Active',
  joinDate: '2023-05-20',
  result: 'Imported',
  reason: null
}, {
  id: 4,
  email: 'david.b@example.org',
  name: 'David Brown',
  plan: 'White Only',
  status: 'Cancelled',
  joinDate: '2021-08-15',
  result: 'Imported',
  reason: null
}, {
  id: 5,
  email: 'lisa.k@example.com',
  name: 'Lisa Kim',
  plan: 'Club 6',
  status: 'Active',
  joinDate: '2023-09-01',
  result: 'Imported',
  reason: null
}, {
  id: 6,
  email: 'james.w@test.com',
  name: 'James Wilson',
  plan: 'Reserve Club',
  status: 'Paused',
  joinDate: '2024-01-10',
  result: 'Merged',
  reason: 'Duplicate contact merged'
}, {
  id: 7,
  email: '',
  name: 'Unknown User',
  plan: 'Club 6',
  status: 'Paused',
  joinDate: '2024-03-10',
  result: 'Skipped',
  reason: 'Missing required field: Email'
}, {
  id: 8,
  email: 'bad@format',
  name: 'Invalid Record',
  plan: '-',
  status: '-',
  joinDate: '-',
  result: 'Skipped',
  reason: 'Invalid email format'
}];
const FIELD_MAPPINGS = [{
  source: 'customer_email',
  target: 'Email',
  status: 'mapped'
}, {
  source: 'first_name',
  target: 'First Name',
  status: 'mapped'
}, {
  source: 'last_name',
  target: 'Last Name',
  status: 'mapped'
}, {
  source: 'created_at',
  target: 'Join Date',
  status: 'mapped'
}, {
  source: 'subscription_plan',
  target: 'Subscription Plan',
  status: 'mapped'
}, {
  source: 'status',
  target: 'Status',
  status: 'mapped'
}, {
  source: 'next_billing',
  target: 'Next Billing Date',
  status: 'mapped'
}, {
  source: 'price',
  target: 'Price',
  status: 'mapped'
}, {
  source: 'phone',
  target: 'Phone',
  status: 'mapped'
}, {
  source: 'loyalty_points',
  target: 'Skipped',
  status: 'skipped'
}, {
  source: 'custom_field_1',
  target: 'Skipped',
  status: 'skipped'
}, {
  source: 'referral_code',
  target: 'Skipped',
  status: 'skipped'
}, {
  source: 'internal_notes',
  target: 'Skipped',
  status: 'skipped'
}, {
  source: 'legacy_id',
  target: 'Skipped',
  status: 'skipped'
}];
const TIMELINE_EVENTS = [{
  text: 'Migration completed — 831 records imported successfully',
  time: '12 Jan 2026, 2:34 PM',
  color: 'bg-[#10B981]'
}, {
  text: '4 records skipped due to validation errors',
  time: '12 Jan 2026, 2:34 PM',
  color: 'bg-[#EF4444]'
}, {
  text: '12 duplicate records merged with existing contacts',
  time: '12 Jan 2026, 2:33 PM',
  color: 'bg-[#F59E0B]'
}, {
  text: 'Field mapping confirmed — 9 of 14 columns mapped',
  time: '12 Jan 2026, 2:30 PM',
  color: 'bg-[#3B82F6]'
}, {
  text: 'File uploaded — recharge_export_jan2026.csv (847 rows detected)',
  time: '12 Jan 2026, 2:29 PM',
  color: 'bg-[#3B82F6]'
}, {
  text: 'Migration created by Sarah Chen',
  time: '12 Jan 2026, 2:28 PM',
  color: 'bg-[#3B82F6]'
}];
export function MigrationDetailPage() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [activeTab, setActiveTab] = useState<'All' | 'Warnings' | 'Errors'>('All');
  // Filter logic
  const filteredResults = IMPORT_RESULTS.filter(row => {
    if (activeTab === 'All') return true;
    if (activeTab === 'Warnings') return row.result === 'Merged';
    if (activeTab === 'Errors') return row.result === 'Skipped';
    return true;
  });
  const getResultBadgeVariant = (result: string) => {
    switch (result) {
      case 'Imported':
        return 'success';
      case 'Merged':
        return 'warning';
      case 'Skipped':
        return 'error';
      default:
        return 'default';
    }
  };
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Paused':
        return 'warning';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-7149">
      {/* Header Section */}
      <div className="mb-6" data-id="element-7150">
        <div className="flex items-center gap-2 text-sm text-[#64748B] mb-2" data-id="element-7151">
          <button onClick={() => navigate('/management/migration')} className="hover:text-[#0F172A] transition-colors" data-id="element-7152">
            Migration
          </button>
          <span data-id="element-7153">/</span>
          <span data-id="element-7154">Recharge Subscriber Import</span>
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-7155">
          <div className="flex items-center gap-3" data-id="element-7156">
            <h1 className="text-2xl font-bold text-[#0F172A]" data-id="element-7157">
              Recharge Subscriber Import — January 2026
            </h1>
            <Badge variant="success" data-id="element-7158">Completed</Badge>
          </div>
          <div className="flex items-center gap-4" data-id="element-7159">
            <button onClick={() => console.log('Delete migration')} className="text-sm font-medium text-[#EF4444] hover:text-[#DC2626] transition-colors" data-id="element-7160">
              Delete Migration
            </button>
            <Button variant="secondary" iconLeft={<Download className="w-4 h-4" data-id="element-7162" />} onClick={() => console.log('Download report')} data-id="element-7161">
              Download Report
            </Button>
          </div>
        </div>
      </div>

      {/* Metadata Strip */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg px-6 py-4 mb-8 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm shadow-sm" data-id="element-7163">
        <div className="flex items-center gap-2" data-id="element-7164">
          <span className="text-[#64748B]" data-id="element-7165">Source:</span>
          <span className="font-medium text-[#0F172A]" data-id="element-7166">Recharge</span>
        </div>
        <div className="w-px h-4 bg-[#E2E8F0] hidden sm:block" data-id="element-7167" />
        <div className="flex items-center gap-2" data-id="element-7168">
          <span className="text-[#64748B]" data-id="element-7169">Date:</span>
          <span className="font-medium text-[#0F172A]" data-id="element-7170">12 Jan 2026</span>
        </div>
        <div className="w-px h-4 bg-[#E2E8F0] hidden sm:block" data-id="element-7171" />
        <div className="flex items-center gap-2" data-id="element-7172">
          <span className="text-[#64748B]" data-id="element-7173">Duration:</span>
          <span className="font-medium text-[#0F172A]" data-id="element-7174">4m 32s</span>
        </div>
        <div className="w-px h-4 bg-[#E2E8F0] hidden sm:block" data-id="element-7175" />
        <div className="flex items-center gap-2" data-id="element-7176">
          <span className="text-[#64748B]" data-id="element-7177">File:</span>
          <span className="font-medium text-[#0F172A]" data-id="element-7178">
            recharge_export_jan2026.csv
          </span>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-7179">
        <StatCard label="Total Records" value="847" data-id="element-7180" />
        <StatCard label="Successfully Imported" value="831" icon={<CheckCircle className="w-6 h-6 text-[#10B981]" data-id="element-7182" />} data-id="element-7181" />
        <StatCard label="Warnings" value="12" icon={<AlertTriangle className="w-6 h-6 text-[#F59E0B]" data-id="element-7184" />} data-id="element-7183" />
        <StatCard label="Errors" value="4" icon={<XCircle className="w-6 h-6 text-[#EF4444]" data-id="element-7186" />} data-id="element-7185" />
      </div>

      {/* Two-Column Row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8" data-id="element-7187">
        {/* Left: Import Results */}
        <div className="lg:col-span-3" data-id="element-7188">
          <Card padding="none" className="h-full flex flex-col" data-id="element-7189">
            <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-7190">
              <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-7191">
                Import Results
              </h2>
            </div>

            {/* Tabs */}
            <div className="px-6 border-b border-[#E2E8F0] flex gap-6" data-id="element-7192">
              {[{
              id: 'All',
              label: 'All (847)'
            }, {
              id: 'Warnings',
              label: 'Warnings (12)'
            }, {
              id: 'Errors',
              label: 'Errors (4)'
            }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-7193">
                  {tab.label}
                </button>)}
            </div>

            {/* Table */}
            <div className="flex-1 overflow-x-auto" data-id="element-7194">
              <table className="w-full text-sm text-left" data-id="element-7195">
                <thead className="bg-[#F8FAFC] text-[#64748B] font-medium border-b border-[#E2E8F0]" data-id="element-7196">
                  <tr data-id="element-7197">
                    <th className="px-6 py-3 font-semibold" data-id="element-7198">Email</th>
                    <th className="px-6 py-3 font-semibold" data-id="element-7199">Name</th>
                    <th className="px-6 py-3 font-semibold" data-id="element-7200">Plan</th>
                    <th className="px-6 py-3 font-semibold" data-id="element-7201">Status</th>
                    <th className="px-6 py-3 font-semibold" data-id="element-7202">Join Date</th>
                    <th className="px-6 py-3 font-semibold" data-id="element-7203">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-7204">
                  {filteredResults.map(row => <tr key={row.id} className="hover:bg-[#F8FAFC] group" data-id="element-7205">
                      <td className="px-6 py-4" data-id="element-7206">
                        <span className={!row.email ? 'text-[#EF4444] italic' : 'text-[#0F172A]'} data-id="element-7207">
                          {row.email || 'Missing'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[#0F172A]" data-id="element-7208">{row.name}</td>
                      <td className="px-6 py-4 text-[#64748B]" data-id="element-7209">{row.plan}</td>
                      <td className="px-6 py-4" data-id="element-7210">
                        {row.status !== '-' && <Badge variant={getStatusBadgeVariant(row.status)} data-id="element-7211">
                            {row.status}
                          </Badge>}
                      </td>
                      <td className="px-6 py-4 text-[#64748B]" data-id="element-7212">
                        {row.joinDate}
                      </td>
                      <td className="px-6 py-4 relative" data-id="element-7213">
                        <div className="inline-block cursor-help" data-id="element-7214">
                          <Badge variant={getResultBadgeVariant(row.result)} data-id="element-7215">
                            {row.result}
                          </Badge>
                          {row.reason && <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-[#1E293B] text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-7216">
                              {row.reason}
                            </div>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-between" data-id="element-7217">
              <span className="text-sm text-[#64748B]" data-id="element-7218">
                Showing 1-{filteredResults.length} of 847 records
              </span>
              <div className="flex gap-2" data-id="element-7219">
                <Button variant="secondary" size="sm" disabled iconLeft={<ChevronLeft className="w-4 h-4" data-id="element-7221" />} data-id="element-7220">
                  Previous
                </Button>
                <Button variant="secondary" size="sm" iconRight={<ChevronRight className="w-4 h-4" data-id="element-7223" />} data-id="element-7222">
                  Next
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Right: Field Mapping */}
        <div className="lg:col-span-2" data-id="element-7224">
          <Card padding="none" className="h-full" data-id="element-7225">
            <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-7226">
              <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-7227">
                Field Mapping Used
              </h2>
            </div>
            <div className="divide-y divide-[#E2E8F0]" data-id="element-7228">
              {FIELD_MAPPINGS.map((mapping, idx) => <div key={idx} className="px-6 py-3 flex items-center justify-between text-sm" data-id="element-7229">
                  <span className="font-mono text-xs bg-[#F1F5F9] px-2 py-1 rounded text-[#475569]" data-id="element-7230">
                    {mapping.source}
                  </span>
                  <div className="flex items-center gap-2" data-id="element-7231">
                    <span className={mapping.status === 'skipped' ? 'text-[#94A3B8]' : 'text-[#0F172A] font-medium'} data-id="element-7232">
                      {mapping.target}
                    </span>
                    {mapping.status === 'mapped' ? <CheckCircle className="w-4 h-4 text-[#10B981]" data-id="element-7233" /> : <Minus className="w-4 h-4 text-[#94A3B8]" data-id="element-7234" />}
                  </div>
                </div>)}
            </div>
          </Card>
        </div>
      </div>

      {/* Timeline Row */}
      <Card padding="none" data-id="element-7235">
        <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-7236">
          <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-7237">
            Import Timeline
          </h2>
        </div>
        <div className="p-6" data-id="element-7238">
          <div className="relative pl-8 space-y-8 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-0.5 before:bg-[#E2E8F0]" data-id="element-7239">
            {TIMELINE_EVENTS.map((event, idx) => <div key={idx} className="relative flex items-start justify-between group" data-id="element-7240">
                <div className="flex items-start gap-4" data-id="element-7241">
                  <div className={`absolute -left-8 w-2.5 h-2.5 rounded-full mt-1.5 ring-4 ring-white ${event.color}`} data-id="element-7242" />
                  <span className="text-sm text-[#0F172A]" data-id="element-7243">{event.text}</span>
                </div>
                <span className="text-xs text-[#64748B] whitespace-nowrap ml-4" data-id="element-7244">
                  {event.time}
                </span>
              </div>)}
          </div>
        </div>
      </Card>
    </div>;
}